
import { supabase } from "@/integrations/supabase/client";
import { Json, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import { Rule, RuleAction, RuleCondition } from "@/lib/types";
import { toast } from "sonner";


interface DbRule {
    id: string;
    tenant_id: string;
    mailbox_id: string;
    name: string;
    order_number: number;
    enabled: boolean;
    conditions: Json;
    actions: Json;
    created_at: string;
    updated_at: string;
  }

type DbRuleInsert = TablesInsert<"rules">;
type DbRuleUpdate = TablesUpdate<"rules">;

function mapRuleFromDb(raw: DbRule): Rule {
    return {
        ...raw,
        order: raw.order_number,
        actions: raw.actions as unknown as RuleAction[],
        conditions: raw.conditions as unknown as RuleCondition[],
    };
}

function mapRuleToDbInsertOrUpdate<T extends Omit<Rule, "id" | "created_at" | "updated_at"> | Partial<Rule>>(rule: T): Omit<T, "actions" | "conditions" | "order"> & { order_number?: number; actions?: Json; conditions?: Json } {
    const { order, actions, conditions, ...rest } = rule as any;
    return {
        ...rest,
        order_number: order,
        actions: actions as unknown as Json,
        conditions: conditions as unknown as Json,
    };
}

const mapRuleToDbInsert = (rule: Omit<Rule, "id" | "created_at" | "updated_at">): DbRuleInsert => mapRuleToDbInsertOrUpdate(rule) as DbRuleInsert;
const mapRuleToDbUpdate = (rule: Partial<Rule>): DbRuleUpdate => mapRuleToDbInsertOrUpdate(rule) as DbRuleUpdate;

export async function getRules(tenantId: string, mailboxId: string): Promise<Rule[]> {
  try {
    const { data, error } = await supabase
      .from("rules")
      .select("*")
      .eq("tenant_id", tenantId)
      .eq("mailbox_id", mailboxId)
      .order("order_number", { ascending: true });

    if (error) {
      toast.error("Failed to fetch rules");
      console.error("Error fetching rules:", error);
      return [];
    }

    return data ? data.map(mapRuleFromDb) : [];
  } catch (error) {
    toast.error("Failed to fetch rules");
    console.error("Error in getRules:", error);
    return [];
  }
}

async function getNextOrderNumber(tenantId: string, mailboxId: string): Promise<number> {
  try {
    const { data, error } = await supabase
      .from("rules")
      .select("order_number")
      .eq("tenant_id", tenantId)
      .eq("mailbox_id", mailboxId)
      .order("order_number", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) {
      console.error("Error getting next order number:", error);
      return 0;
    }

    // If no rules exist, start with 0, otherwise increment highest order
    return data ? data.order_number + 1 : 0;
  } catch (error) {
    console.error("Error in getNextOrderNumber:", error);
    return 0;
  }
}

export async function createRule(rule: Omit<Rule, "id" | "created_at" | "updated_at">): Promise<Rule | null> {
  try {
    // Get the next available order number
    const orderNumber = await getNextOrderNumber(rule.tenant_id, rule.mailbox_id);
    
    // Create rule with the calculated order number
    const ruleWithOrder = { ...rule, order: orderNumber };
    
    const { data, error } = await supabase
      .from("rules")
      .insert(mapRuleToDbInsert(ruleWithOrder))
      .select("*")
      .single();

    if (error) {
      toast.error("Failed to create rule");
      console.error("Error creating rule:", error);
      return null;
    }

    toast.success("Rule created successfully");
    return mapRuleFromDb(data) || null;
  } catch (error) {
    toast.error("Failed to create rule");
    console.error("Error in createRule:", error);
    return null;
  }
}

export async function updateRule(rule: Partial<Rule> & { id: string }): Promise<Rule | null> {
    try {
      const { data, error } = await supabase
        .from("rules")
        .update(mapRuleToDbUpdate(rule))
        .eq("id", rule.id)
        .select("*")
        .single();
  
      if (error) {
        toast.error("Failed to update rule");
        console.error("Error updating rule:", error);
        return null;
      }
  
      toast.success("Rule updated successfully");
      return mapRuleFromDb(data) || null;
    } catch (error) {
      toast.error("Failed to update rule");
      console.error("Error in updateRule:", error);
      return null;
    }
  }

export async function deleteRule(ruleId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from("rules")
      .delete()
      .eq("id", ruleId);

    if (error) {
      toast.error("Failed to delete rule");
      console.error("Error deleting rule:", error);
      return false;
    }

    toast.success("Rule deleted successfully");
    return true;
  } catch (error) {
    toast.error("Failed to delete rule");
    console.error("Error in deleteRule:", error);
    return false;
  }
}

export async function updateRuleOrder(rules: { id: string, order_number: number }[]): Promise<boolean> {
  try {
    // Use Promise.all to run all updates in parallel
    const updates = rules.map(rule => 
      supabase
        .from("rules")
        .update({ order_number: rule.order_number })
        .eq("id", rule.id)
    );
    
    const results = await Promise.all(updates);
    
    // Check if any update had an error
    const hasError = results.some(result => result.error);
    
    if (hasError) {
      const errors = results
        .filter(result => result.error)
        .map(result => result.error?.message)
        .join(", ");
      
      console.error("Error updating rule order:", errors);
      toast.error("Failed to update rule order");
      return false;
    }
    
    return true;
  } catch (error) {
    toast.error("Failed to update rule order");
    console.error("Error in updateRuleOrder:", error);
    return false;
  }
}

export async function toggleRuleStatus(ruleId: string, enabled: boolean): Promise<boolean> {
  try {
    const { error } = await supabase
      .from("rules")
      .update({ enabled })
      .eq("id", ruleId);

    if (error) {
      toast.error("Failed to update rule status");
      console.error("Error updating rule status:", error);
      return false;
    }

    toast.success(`Rule ${enabled ? "enabled" : "disabled"} successfully`);
    return true;
  } catch (error) {
    toast.error("Failed to update rule status");
    console.error("Error in toggleRuleStatus:", error);
    return false;
  }
}
